import { useState, useCallback, useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '@/config/api';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt?: Date;
}

interface UseStreamingChatOptions {
  apiEndpoint?: string;
  useMock?: boolean; // Set to true to use mock responses (for testing)
  /** When set, send this message once as the first user message (e.g. from waitlist "Chat with Saxa") */
  initialMessage?: string | null;
}

// Mock AI responses for testing
const mockResponses = [
  "xReplAI is an AI-powered Chrome extension that helps you draft smart, personalized email replies for Gmail and LinkedIn. It saves you time and helps build better professional relationships!",
  "Great question! xReplAI works seamlessly in both Gmail and LinkedIn. Just install the extension, and when you're composing a reply, click the xReplAI button to generate a smart response based on the context of the conversation.",
  "We offer four pricing tiers: Free (5 replies/month), Professional ($9.99/month with 100 replies), Business ($29.99/month with 500 replies), and Enterprise (custom pricing with unlimited replies). You can start with our free tier to try it out!",
  "xReplAI is perfect for Sales professionals, Recruiters, and Job Seekers. It helps you respond faster while maintaining a personal touch in every message.",
  "Yes! You can join our waitlist right here on the website. Just scroll down and enter your email - we'll notify you as soon as we launch!",
];

function getMockResponse(userMessage: string): string {
  const lower = userMessage.toLowerCase();
  
  if (lower.includes('price') || lower.includes('cost') || lower.includes('plan')) {
    return mockResponses[2];
  }
  if (lower.includes('how') || lower.includes('work')) {
    return mockResponses[1];
  }
  if (lower.includes('who') || lower.includes('for whom') || lower.includes('user')) {
    return mockResponses[3];
  }
  if (lower.includes('waitlist') || lower.includes('sign up') || lower.includes('join')) {
    return mockResponses[4];
  }
  
  // Default response
  return mockResponses[0];
}

export function useStreamingChat(options: UseStreamingChatOptions = {}) {
  const { apiEndpoint = API_ENDPOINTS.SALES_CHAT, useMock = false, initialMessage } = options;
  const initialMessageSentRef = useRef(false);

  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: "Hey! I'm Saxa — I know xReplAI inside and out. Features, pricing, how it works — ask me anything.",
      createdAt: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!input.trim() || isLoading) return;

      const userMessage: Message = {
        id: Date.now().toString(),
        role: 'user',
        content: input,
        createdAt: new Date(),
      };

      // Add user message
      setMessages((prev) => [...prev, userMessage]);
      setInput('');
      setIsLoading(true);

      try {
        if (useMock) {
          // Use mock response
          const mockResponse = getMockResponse(input);
          const assistantId = (Date.now() + 1).toString();

          // Add empty assistant message that we'll update
          setMessages((prev) => [
            ...prev,
            {
              id: assistantId,
              role: 'assistant',
              content: '',
              createdAt: new Date(),
            },
          ]);

          // Simulate streaming response
          let assistantMessage = '';
          const chunks = mockResponse.split(' ');
          for (const chunk of chunks) {
            assistantMessage += chunk + ' ';
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: assistantMessage }
                  : m
              )
            );
            await new Promise(resolve => setTimeout(resolve, 100)); // Simulate delay
          }
        } else {
          // Use real API
          const payload = {
            messages: [...messages, userMessage].map((m) => ({
              role: m.role,
              content: m.content,
            })),
          };
          console.log('[Chat] POST', apiEndpoint, payload);
          const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          });

          if (!response.ok) {
            const text = await response.text();
            if (import.meta.env.DEV) {
              console.error('[Chat] HTTP error', response.status, text);
            }
            throw new Error(`HTTP ${response.status}: ${text.slice(0, 200)}`);
          }

          const reader = response.body?.getReader();
          if (!reader) {
            if (import.meta.env.DEV) {
              console.error('[Chat] No response body (possible CORS block)');
            }
            throw new Error('No response body — check CORS or try again.');
          }

          const decoder = new TextDecoder();
          let assistantMessage = '';
          const assistantId = (Date.now() + 1).toString();

          setMessages((prev) => [
            ...prev,
            {
              id: assistantId,
              role: 'assistant',
              content: '',
              createdAt: new Date(),
            },
          ]);

          // Plain-text stream from toTextStreamResponse(): append each chunk
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            if (chunk) {
              assistantMessage += chunk;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: assistantMessage }
                    : m
                )
              );
            }
          }

          if (!assistantMessage && import.meta.env.DEV) {
            console.warn('[Chat] Stream ended with no content');
          }
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        console.error('[Chat] Error:', errMsg, error); // always log so production shows failure
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content:
              import.meta.env.DEV && errMsg
                ? `Connection error: ${errMsg}`
                : "I'm sorry, I'm having trouble connecting right now. Please try again later or contact support at admin@blckrbbt.com",
            createdAt: new Date(),
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [input, isLoading, messages, apiEndpoint, useMock]
  );

  // When initialMessage is set (e.g. from waitlist "Chat with Saxa" redirect), send it once as first user message
  useEffect(() => {
    if (!initialMessage?.trim() || initialMessageSentRef.current) return;
    initialMessageSentRef.current = true;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: initialMessage.trim(),
      createdAt: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    const apiMessages = [...messages, userMessage].map((m) => ({ role: m.role, content: m.content }));

    (async () => {
      try {
        if (useMock) {
          const mockResponse = getMockResponse(initialMessage.trim());
          const assistantId = (Date.now() + 1).toString();
          setMessages((prev) => [
            ...prev,
            { id: assistantId, role: 'assistant', content: '', createdAt: new Date() },
          ]);
          let assistantMessage = '';
          for (const chunk of mockResponse.split(' ')) {
            assistantMessage += chunk + ' ';
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantId ? { ...m, content: assistantMessage } : m))
            );
            await new Promise((r) => setTimeout(r, 100));
          }
        } else {
          if (import.meta.env.DEV) {
            console.log('[Chat] POST (initialMessage)', apiEndpoint, { messages: apiMessages });
          }
          const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messages: apiMessages }),
          });
          if (!response.ok) {
            const text = await response.text();
            if (import.meta.env.DEV) console.error('[Chat] HTTP error', response.status, text);
            throw new Error(`HTTP ${response.status}`);
          }
          const reader = response.body?.getReader();
          if (!reader) {
            if (import.meta.env.DEV) console.error('[Chat] No response body (initialMessage)');
            throw new Error('No response body');
          }
          const decoder = new TextDecoder();
          let assistantMessage = '';
          const assistantId = (Date.now() + 1).toString();
          setMessages((prev) => [
            ...prev,
            { id: assistantId, role: 'assistant', content: '', createdAt: new Date() },
          ]);
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            const chunk = decoder.decode(value, { stream: true });
            if (chunk) {
              assistantMessage += chunk;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? { ...m, content: assistantMessage } : m
                )
              );
            }
          }
        }
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        console.error('[Chat] Initial message error:', errMsg, error);
        setMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 1).toString(),
            role: 'assistant',
            content:
              import.meta.env.DEV && errMsg
                ? `Connection error: ${errMsg}`
                : "I'm sorry, I'm having trouble connecting right now. Please try again later or contact support.",
            createdAt: new Date(),
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [initialMessage, apiEndpoint, useMock]);

  return {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
  };
}